import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Video, TrendingUp, Eye, PlayCircle, Loader2 } from "lucide-react";
import {
  HeroCard, StatCard, ChartCard, InsightCard, Toolbar, SkeletonCard, EmptyState
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { API_URL } from "@/utils/apiConfig";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-slate-400 font-medium">{payload[0]?.payload?.date || ''}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold text-white">
          {p.name}: {typeof p.value === "number" ? p.value.toFixed(0) : p.value}
        </p>
      ))}
    </div>
  );
};

export function VideoGenerationTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [prompt, setPrompt] = useState("");
  const [size, setSize] = useState("1280x720");
  const [duration, setDuration] = useState("4");
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId || status === "done" || status === "error") return;
    const interval = setInterval(async () => {
      try {
        const res = await authAxiosRef.current.get(`/video/status/${jobId}`);
        setStatus(res.data.status);
        if (res.data.status === "done") { clearInterval(interval); toast.success("Vídeo gerado!"); }
        if (res.data.status === "error") { clearInterval(interval); toast.error("Erro ao gerar vídeo"); }
      } catch { clearInterval(interval); }
    }, 5000);
    return () => clearInterval(interval);
  }, [jobId, status]);

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error("Descreva o vídeo"); return; }
    setGenerating(true);
    try {
      const res = await authAxios.post("/video/generate", { prompt, size, duration: parseInt(duration) });
      setJobId(res.data.job_id);
      setStatus("queued");
      toast.success("Geração iniciada!");
    } catch (err) {
      toast.error("Erro ao gerar vídeo");
    }
    setGenerating(false);
  };

  const totalVideos = 23;
  const publishedVideos = 18;
  const totalViews = 4587;
  const engagementRate = 7.2;

  const timelineData = [
    { date: 'D1', views: 342 },
    { date: 'D2', views: 521 },
    { date: 'D3', views: 387 },
    { date: 'D4', views: 698 },
    { date: 'D5', views: 456 },
    { date: 'D6', views: 812 },
    { date: 'D7', views: 625 },
  ];

  if (loading) return <SkeletonCard count={4} type="stat" />;

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Vídeos IA"
        metric={publishedVideos.toString()}
        subMetrics={[
          { label: "Total Gerados", value: totalVideos.toString() },
          { label: "Visualizações", value: totalViews.toString(), trend: "up", change: 15 },
          { label: "Engajamento", value: `${engagementRate.toFixed(1)}%`, trend: "up", change: 3 },
        ]}
        color={MARKETING_COLORS.WhatsApp}
      />

      {/* Period Selector */}
      <Toolbar period={period} onPeriodChange={setPeriod} />

      {/* KPI Cards */}
      <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-4">
        <StatCard label="Vídeos Gerados" value={totalVideos.toString()} icon={Video} trend="up" change={8} />
        <StatCard label="Publicados" value={publishedVideos.toString()} icon={PlayCircle} trend="neutral" change={0} />
        <StatCard label="Visualizações" value={totalViews.toLocaleString()} icon={Eye} trend="up" change={12} />
        <StatCard label="Engajamento" value={`${engagementRate.toFixed(1)}%`} icon={TrendingUp} trend="up" change={4} />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Distribuição" subtitle="Gerados vs Publicados" height="300px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={[{ name: "Gerados", value: totalVideos }, { name: "Publicados", value: publishedVideos }]}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="name" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={MARKETING_COLORS.WhatsApp} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Visualizações" subtitle="Últimos 7 dias" height="300px">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timelineData}>
              <defs>
                <linearGradient id="gradVideos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={MARKETING_COLORS.WhatsApp} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={MARKETING_COLORS.WhatsApp} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="views" name="Visualizações" stroke={MARKETING_COLORS.WhatsApp} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Insights */}
      <div className="grid md:grid-cols-3 gap-4">
        <InsightCard type="success" title="Taxa Publicação" message={`${((publishedVideos / totalVideos) * 100).toFixed(0)}% aproveitamento — Excelente!`} />
        <InsightCard type={engagementRate > 5 ? "success" : "insight"} title="Engajamento" message={`${engagementRate.toFixed(1)}% — ${engagementRate > 5 ? "Forte" : "Otimize"}`} />
        <InsightCard type="insight" title="Recomendação" message="Vídeos verticais têm maior engajamento em redes sociais" />
      </div>

      {/* Configuration Section */}
      <div className="space-y-6 pt-6 border-t">
        <h2 className="text-lg font-semibold">Gerar Novo Vídeo</h2>

        <Card className="bg-card border-border/50">
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Descrição do Vídeo</Label>
              <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Ex: Vídeo cinematográfico de sushi fresco..." className="text-sm min-h-[80px]" disabled={status === "queued" || status === "generating"} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Resolução</Label>
                <Select value={size} onValueChange={setSize} disabled={status === "queued" || status === "generating"}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1280x720">HD (1280x720)</SelectItem>
                    <SelectItem value="1792x1024">Widescreen</SelectItem>
                    <SelectItem value="1024x1792">Vertical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duração</Label>
                <Select value={duration} onValueChange={setDuration} disabled={status === "queued" || status === "generating"}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4">4s</SelectItem>
                    <SelectItem value="8">8s</SelectItem>
                    <SelectItem value="12">12s</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={handleGenerate} disabled={generating || !prompt.trim() || status === "queued" || status === "generating"} className="w-full gap-2" size="lg">
              {status === "queued" || status === "generating" ? <><Loader2 className="w-4 h-4 animate-spin" />Gerando...</> : <>Gerar Vídeo</>}
            </Button>
          </CardContent>
        </Card>

        {/* Status */}
        {status && (
          <Card className={`border-border/50 ${status === "done" ? "bg-emerald-50/50" : status === "error" ? "bg-red-50/50" : "bg-blue-50/50"}`}>
            <CardContent className="p-4">
              <p className="text-sm font-medium">
                {status === "queued" && "⏳ Fila..."}
                {status === "generating" && "⚙️ Gerando... (2-5 min)"}
                {status === "done" && "✓ Vídeo pronto!"}
                {status === "error" && "✕ Erro"}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
